<template>
  <div data-testid="accruals-column-mapping-panel" class="accruals-column-mapping-panel">
    <nav
      data-testid="accruals-column-mapping-panel__nav"
      class="accruals-column-mapping-panel__nav"
      aria-label="Column sections"
    >
      <p class="accruals-column-mapping-panel__nav-heading">COLUMNS</p>
      <button
        v-for="item in columnNav"
        :key="item.key"
        type="button"
        :data-testid="`accruals-column-mapping-panel__nav-item--${item.key}`"
        class="accruals-column-mapping-panel__nav-item"
        :class="{ 'accruals-column-mapping-panel__nav-item--active': activeSection === item.key }"
        @click="onSectionChange(item.key)"
      >
        {{ item.label }}
      </button>
    </nav>

    <div
      data-testid="accruals-column-mapping-panel__content"
      class="accruals-column-mapping-panel__content"
    >
      <p v-if="!projectId" class="accruals-column-mapping-panel__field-hint">
        Add a <strong>project</strong> query parameter to load column mapping for this cycle.
      </p>

      <v-skeleton-loader
        v-else-if="loading"
        type="article"
        class="accruals-column-mapping-panel__skeleton"
        data-testid="accruals-column-mapping-panel__skeleton"
      />

      <template v-else>
        <div class="accruals-column-mapping-panel__section">
          <div class="accruals-column-mapping-panel__section-header">
            <h3 class="accruals-column-mapping-panel__section-title">IM REQUEST COLUMN MAPPING</h3>
            <div class="accruals-column-mapping-panel__toggle-row">
              <span class="accruals-column-mapping-panel__toggle-label">Active</span>
              <button
                type="button"
                data-testid="accruals-column-mapping-panel__active-toggle"
                class="accruals-column-mapping-panel__toggle"
                :class="{ 'accruals-column-mapping-panel__toggle--on': isActive }"
                :aria-pressed="isActive"
                :disabled="busy"
                @click="onActiveToggle"
              >
                <span class="accruals-column-mapping-panel__toggle-knob" />
              </button>
            </div>
          </div>

          <div class="accruals-column-mapping-panel__field">
            <label class="accruals-column-mapping-panel__field-label">
              REQUESTED INFORMATION COLUMNS
            </label>
            <accruals-column-tags-input
              v-model="requestColumns"
              :items="columnOptions"
              :disabled="busy"
              data-testid="accruals-column-mapping-panel__request-columns"
              @change="onMappingChange"
            />
          </div>
        </div>

        <div class="accruals-column-mapping-panel__section">
          <div class="accruals-column-mapping-panel__field">
            <label class="accruals-column-mapping-panel__field-label">Details columns</label>
            <accruals-column-tags-input
              v-model="detailColumns"
              :items="columnOptions"
              :disabled="busy"
              data-testid="accruals-column-mapping-panel__detail-columns"
              @change="onMappingChange"
            />
            <p class="accruals-column-mapping-panel__field-hint">
              These columns will appear in the Details icon popover on the IM page
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import AccrualsColumnTagsInput from './AccrualsColumnTagsInput.vue';
import {
  ADMIN_CONSOLE_ACCRUALS_COLUMN_NAV,
  ADMIN_CONSOLE_ACCRUALS_COLUMN_OPTIONS,
  ADMIN_CONSOLE_ACCRUALS_DEFAULT_REQUEST_COLUMNS,
} from '@/modules/admin/utils/adminConsoleConstants';
import {
  loadImColumnMapping,
  saveImColumnMapping,
} from '@/modules/admin/utils/accrualsImColumnMappingStorage';

export default {
  name: 'AccrualsColumnMappingPanel',
  components: {
    AccrualsColumnTagsInput,
  },
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      activeSection: 'info-request',
      columnNav: ADMIN_CONSOLE_ACCRUALS_COLUMN_NAV,
      columnOptions: ADMIN_CONSOLE_ACCRUALS_COLUMN_OPTIONS,
      resolvedProjectId: '',
      loading: false,
      saving: false,
      isActive: true,
      requestColumns: [...ADMIN_CONSOLE_ACCRUALS_DEFAULT_REQUEST_COLUMNS],
      detailColumns: [...ADMIN_CONSOLE_ACCRUALS_COLUMN_OPTIONS],
      imRule: null,
      persistEnabled: false,
    };
  },
  computed: {
    projectId() {
      return (
        this.resolvedProjectId ||
        this.$route.query.project ||
        this.$route.query.projectId ||
        this.$route.params.id ||
        ''
      );
    },
    cid() {
      const client = this.$store.state.client;
      if (client && typeof client === 'object') {
        return client._id || client.id || this.$route.query.cid || '';
      }
      return client || this.$route.query.cid || '';
    },
    defaultDetailColumns() {
      return [...ADMIN_CONSOLE_ACCRUALS_COLUMN_OPTIONS];
    },
    busy() {
      return this.loading || this.saving;
    },
  },
  watch: {
    active(isActive) {
      if (isActive) {
        this.loadSectionData();
      }
    },
    activeSection(newVal, oldVal) {
      if (newVal !== oldVal && this.active) {
        this.loadSectionData();
      }
    },
    cid() {
      if (this.active) {
        this.loadSectionData();
      }
    },
  },
  mounted() {
    if (this.active) {
      this.loadSectionData();
    }
  },
  methods: {
    onSectionChange(sectionKey) {
      if (this.activeSection === sectionKey) return;
      this.activeSection = sectionKey;
    },
    async resolveProjectId() {
      const fromRoute =
        this.$route.query.project ||
        this.$route.query.projectId ||
        this.$route.params.id ||
        '';
      if (fromRoute) {
        this.resolvedProjectId = String(fromRoute);
        return this.resolvedProjectId;
      }

      const cachedCycles = this.$store.state.m_financialclose?.cyclesForSwitcher;
      if (Array.isArray(cachedCycles) && cachedCycles.length) {
        this.resolvedProjectId = String(cachedCycles[0]._id || '');
        return this.resolvedProjectId;
      }

      try {
        const cycles = await this.$store.dispatch('fetchCyclesForSwitcher');
        if (Array.isArray(cycles) && cycles.length) {
          this.resolvedProjectId = String(cycles[0]._id || '');
          return this.resolvedProjectId;
        }
      } catch (err) {
        console.log('[AccrualsColumnMappingPanel]: resolveProjectId', err);
      }

      this.resolvedProjectId = '';
      return '';
    },
    applyDefaults() {
      this.isActive = true;
      this.requestColumns = [...ADMIN_CONSOLE_ACCRUALS_DEFAULT_REQUEST_COLUMNS];
      this.detailColumns = this.defaultDetailColumns;
    },
    applyStoredMapping(stored) {
      if (!stored) {
        this.applyDefaults();
        return;
      }
      this.isActive = stored.isActive !== false;
      this.requestColumns = Array.isArray(stored.requestColumns)
        ? [...stored.requestColumns]
        : [...ADMIN_CONSOLE_ACCRUALS_DEFAULT_REQUEST_COLUMNS];
      this.detailColumns = Array.isArray(stored.detailColumns)
        ? [...stored.detailColumns]
        : this.defaultDetailColumns;
    },
    mergeImRulePayload(imRule) {
      if (!imRule) return;
      if (imRule.status === 'disabled') {
        this.isActive = false;
      } else if (imRule.status === 'saved') {
        this.isActive = true;
      }
      if (Array.isArray(imRule.payload_columns) && imRule.payload_columns.length) {
        this.detailColumns = [...imRule.payload_columns];
      }
    },
    async loadSectionData() {
      const projectId = await this.resolveProjectId();
      if (!projectId) {
        this.applyDefaults();
        return;
      }

      this.loading = true;
      this.persistEnabled = false;
      try {
        const stored = loadImColumnMapping(projectId);
        this.applyStoredMapping(stored);

        try {
          const imRule = await this.$store.dispatch('accruals/loadImRule', { projectId });
          this.imRule = imRule || null;
          if (!stored) {
            this.mergeImRulePayload(imRule);
          }
        } catch (err) {
          console.log('[AccrualsColumnMappingPanel]: loadImRule', err);
        }
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.persistEnabled = true;
        });
      }
    },
    persistMapping() {
      if (!this.persistEnabled || !this.projectId) return;
      saveImColumnMapping(this.projectId, {
        isActive: this.isActive,
        requestColumns: this.requestColumns,
        detailColumns: this.detailColumns,
      });
    },
    onMappingChange() {
      this.persistMapping();
    },
    async onActiveToggle() {
      this.isActive = !this.isActive;
      this.persistMapping();
      await this.syncImRuleStatus();
    },
    async syncImRuleStatus() {
      const conditions = this.imRule?.conditions || [];
      if (!this.projectId || !conditions.length) return;

      this.saving = true;
      try {
        const data = await this.$store.dispatch('accruals/saveImRule', {
          projectId: this.projectId,
          conditions,
          ownerResolution: this.imRule?.owner_resolution,
          payloadColumns: this.detailColumns,
          status: this.isActive ? 'saved' : 'disabled',
        });
        this.imRule = data || this.imRule;
      } catch (err) {
        console.log('[AccrualsColumnMappingPanel]: syncImRuleStatus', err);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./AccrualsColumnMappingPanel.scss"></style>
